import { useMemo, useState } from 'react';
import { useParams } from 'react-router';
import {
  Badge,
  EmptyState,
  Panel,
  SegmentedControl,
  Tooltip,
  cn,
  PRESS,
  formatDate,
  formatMoney,
  formatPercentage,
  type BadgeTone,
} from 'escrowbuild-ui';

import {
  useMilestones,
  useScheduleOf,
  useT,
  type AppTranslate,
  type AppTranslationKey,
} from '@/i18n';
import { projectTotals, type Milestone, type SchedulePhase } from '@/data/domain';

/* -----------------------------------------------------------------------------
   The schedule's own coordinate system

   `plannedStart`/`plannedWidth` and their actual counterparts are percentages of
   a 12-month axis, not dates — the fixtures have no calendar anchor to convert
   them against, and inventing one here would put a second, private chronology
   next to the one the shell already prints. One month is therefore `100 / 12`
   units, and every position on this page is expressed in those units.
   -------------------------------------------------------------------------- */

/** One month, in the schedule's own percentage units. */
const UNIT = 100 / 12;

/** The label column, shared by the axis header and every row so the ticks line up. */
const ROW_GRID = 'grid grid-cols-[17rem_minmax(0,1fr)] gap-x-4';

const HEAD_CELL =
  'bg-surface-alt px-3 py-2 font-display text-2xs font-semibold uppercase tracking-label text-muted';

const FOCUS_RING =
  'focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-focus';

const STATUS_META: Record<SchedulePhase['status'], { label: AppTranslationKey; tone: BadgeTone }> = {
  completed: { label: 'status.phase.completed', tone: 'success' },
  'in-progress': { label: 'status.phase.inProgress', tone: 'info' },
  planned: { label: 'status.phase.planned', tone: 'neutral' },
};

/** The rows a status filter can select. `all` is the unfiltered view. */
const FILTER_OPTIONS: { value: string; label: AppTranslationKey }[] = [
  { value: 'all', label: 'project.schedule.filter.all' },
  { value: 'completed', label: 'status.phase.completed' },
  { value: 'in-progress', label: 'status.phase.inProgress' },
  { value: 'planned', label: 'status.phase.planned' },
];

interface Span {
  start: number;
  end: number;
}

/**
 * How many months the axis has to cover.
 *
 * Derived rather than fixed at twelve: a project whose phases run past the
 * twelfth month (Birch Hollow reaches 132 units) would have its last two phases
 * drawn past the right edge of a 12-month grid and silently clipped.
 */
function axisMonths(phases: SchedulePhase[]): number {
  const furthest = phases.reduce(
    (max, phase) =>
      Math.max(
        max,
        phase.plannedStart + phase.plannedWidth,
        phase.actualStart === null ? 0 : phase.actualStart + (phase.actualWidth ?? 0),
      ),
    0,
  );
  return Math.max(1, Math.ceil(furthest / UNIT));
}

/** The 1-based month a position falls in, clamped to the axis. */
function monthAt(position: number, months: number): number {
  return Math.min(months, Math.floor(position / UNIT) + 1);
}

/** "month 3" when both edges fall inside one month, "months 7–9" otherwise. */
function monthRange(start: number, end: number, months: number, t: AppTranslate): string {
  const from = monthAt(start, months);
  const to = monthAt(end, months);
  return from === to
    ? t('project.schedule.month', { month: from })
    : t('project.schedule.monthRange', { from, to });
}

/**
 * A phase with nothing spent cannot be under or over budget, so its variance
 * carries no tone — a green "-$312,000" against a phase that has not started
 * reads as a saving the project has not made.
 */
function varianceTone(actual: number, variance: number): string {
  if (actual === 0) return 'text-muted';
  return variance <= 0 ? 'text-success' : 'text-danger';
}

/** A phase's actual window once it has started, its planned window before that. */
function workSpan(phase: SchedulePhase): Span {
  if (phase.actualStart !== null && phase.actualWidth !== null) {
    return { start: phase.actualStart, end: phase.actualStart + phase.actualWidth };
  }
  return { start: phase.plannedStart, end: phase.plannedStart + phase.plannedWidth };
}

const hasActual = (phase: SchedulePhase): boolean =>
  phase.actualStart !== null && phase.actualWidth !== null;

/** Strict intersection: two phases that merely touch share no working day. */
function overlaps(a: SchedulePhase, b: SchedulePhase): boolean {
  const left = workSpan(a);
  const right = workSpan(b);
  return left.start < right.end && right.start < left.end;
}

/**
 * The most phases that are on site at one instant. Swept rather than nested —
 * a closing edge is processed before an opening one at the same position, or two
 * phases that run back to back would be counted as concurrent.
 */
function peakConcurrency(phases: SchedulePhase[]): number {
  const edges = phases
    .flatMap((phase) => {
      const span = workSpan(phase);
      return [
        { at: span.start, delta: 1 },
        { at: span.end, delta: -1 },
      ];
    })
    .sort((a, b) => a.at - b.at || a.delta - b.delta);

  let running = 0;
  let peak = 0;
  for (const edge of edges) {
    running += edge.delta;
    peak = Math.max(peak, running);
  }
  return peak;
}

/**
 * Where a phase's milestones sit on the axis. Milestones carry real dates while
 * the axis carries month offsets, so the marker is placed within the phase's own
 * span, in date order — the position says "inside this phase, in this sequence",
 * never "at this date", and the date travels in the marker's accessible name.
 */
function markerRun(milestones: Milestone[], span: Span) {
  const width = span.end - span.start;
  const ordered = [...milestones].sort((a, b) => a.date.localeCompare(b.date));
  return ordered.map((milestone, index) => ({
    milestone,
    position: span.start + (width * (index + 1)) / (ordered.length + 1),
  }));
}

/* -------------------------------------------------------------------------- */

export function ProjectSchedulePage() {
  const t = useT();
  const { projectId } = useParams();
  const id = projectId ?? '';
  const [status, setStatus] = useState('all');

  /*
     The two lists this screen draws are resolved for the active locale, so a
     phase's name and a milestone's title follow the language selector wherever
     they appear — in the rows, in the parallel-work pairs and in the tooltips.
     The arithmetic above them reads only dates and positions.
  */
  const phases = useScheduleOf(id);
  const milestones = useMilestones(id);
  const totals = useMemo(() => projectTotals(id), [id]);

  // The axis is derived from EVERY phase, never from the filtered set: a filter
  // that re-scaled the grid would make two rows' bars incomparable the moment it
  // changed, which is the opposite of what a timeline is for.
  const months = useMemo(() => axisMonths(phases), [phases]);
  const extent = months * UNIT;

  const visible = useMemo(
    () => (status === 'all' ? phases : phases.filter((phase) => phase.status === status)),
    [phases, status],
  );

  const parallel = useMemo(() => {
    const pairs: { a: SchedulePhase; b: SchedulePhase; start: number; end: number }[] = [];
    for (let i = 0; i < visible.length; i += 1) {
      for (let j = i + 1; j < visible.length; j += 1) {
        const a = visible[i];
        const b = visible[j];
        if (!a || !b || !overlaps(a, b)) continue;
        const left = workSpan(a);
        const right = workSpan(b);
        pairs.push({
          a,
          b,
          start: Math.max(left.start, right.start),
          end: Math.min(left.end, right.end),
        });
      }
    }
    return pairs.sort((a, b) => a.start - b.start);
  }, [visible]);

  const peak = useMemo(() => peakConcurrency(visible), [visible]);
  const spentShare = totals.budgeted > 0 ? (totals.actual / totals.budgeted) * 100 : null;

  return (
    <div className="flex flex-col gap-6">
      <Panel
        title={t('project.schedule.timeline.title')}
        description={t('project.schedule.timeline.description')}
        headerActions={
          <SegmentedControl
            options={FILTER_OPTIONS.map((option) => ({ ...option, label: t(option.label) }))}
            value={status}
            onValueChange={setStatus}
            size="sm"
            label={t('project.schedule.filterLabel')}
          />
        }
      >
        {phases.length === 0 ? (
          <EmptyState
            variant="table"
            title={t('project.schedule.empty.title')}
            description={t('project.schedule.empty.description')}
          />
        ) : (
          <>
            <div className="overflow-x-auto">
              <div className="min-w-[48rem]">
                {/* The axis: one tick per month, centred over the column of grid
                    that month owns, so a bar's edge can be read against a number. */}
                <div className={cn(ROW_GRID, 'border-b border-line-strong pb-1.5')}>
                  <span className="font-display text-2xs font-semibold uppercase tracking-label text-muted">
                    {t('project.schedule.col.phase')}
                  </span>
                  <div aria-hidden="true" className="flex">
                    {Array.from({ length: months }, (_, index) => (
                      <span
                        key={index}
                        className="flex-1 text-center font-display text-2xs tabular-nums text-muted"
                      >
                        {index + 1}
                      </span>
                    ))}
                  </div>
                </div>

                <ul className="flex list-none flex-col p-0">
                  {visible.length === 0 ? (
                    <li className="py-8 text-center text-xs text-muted">
                      {t('project.schedule.noMatch')}
                    </li>
                  ) : (
                    visible.map((phase) => (
                      <PhaseRow
                        key={phase.id}
                        phase={phase}
                        months={months}
                        extent={extent}
                        milestones={milestones.filter((m) => m.phaseId === phase.id)}
                      />
                    ))
                  )}
                </ul>
              </div>
            </div>

            <ul className="mt-3 flex list-none flex-wrap items-center gap-x-4 gap-y-1 p-0 text-2xs text-muted">
              <li className="flex items-center gap-1.5">
                <span aria-hidden="true" className="h-2 w-6 rounded-xs bg-chart-2" />
                {t('status.phase.planned')}
              </li>
              <li className="flex items-center gap-1.5">
                <span aria-hidden="true" className="h-2 w-6 rounded-xs bg-chart-1" />
                {t('project.schedule.legend.actual')}
              </li>
              <li className="flex items-center gap-1.5">
                <span aria-hidden="true" className="size-2 rotate-45 bg-success" />
                {t('project.schedule.legend.reached')}
              </li>
              <li className="flex items-center gap-1.5">
                <span
                  aria-hidden="true"
                  className="size-2 rotate-45 border border-line-strong bg-surface"
                />
                {t('project.schedule.legend.upcoming')}
              </li>
            </ul>

            <p className="mt-2 text-2xs text-muted">
              {t('project.schedule.note.axis')}{' '}
              {t('project.schedule.note.reach', { month: months })}{' '}
              {spentShare === null
                ? null
                : t('project.schedule.note.spent', {
                    value: formatPercentage(spentShare, { precision: 1 }),
                  })}
            </p>

            <div className="mt-5 border-t border-line-subtle pt-4">
              <h4 className="font-display text-2xs font-semibold uppercase tracking-label text-muted">
                {t('project.schedule.parallel.title')}
              </h4>
              {parallel.length === 0 ? (
                <p className="mt-2 text-xs text-muted">{t('project.schedule.parallel.none')}</p>
              ) : (
                <>
                  <p className="mt-1.5 text-xs text-body">
                    {t('project.schedule.parallel.summary', {
                      count: parallel.length,
                      phases: visible.length,
                      fronts: peak,
                    })}{' '}
                    {t('project.schedule.parallel.rules')}
                  </p>
                  <ul className="mt-2 grid list-none grid-cols-1 gap-x-8 gap-y-1 p-0 sm:grid-cols-2">
                    {parallel.map(({ a, b, start, end }) => (
                      <li key={`${a.id}-${b.id}`} className="text-xs text-body">
                        <span className="font-medium text-heading">{a.name}</span>
                        <span className="text-muted"> {t('project.schedule.parallel.with')} </span>
                        <span className="font-medium text-heading">{b.name}</span>
                        <span className="tabular-nums text-muted">
                          {' '}
                          — {monthRange(start, end, months, t)}
                        </span>
                      </li>
                    ))}
                  </ul>
                </>
              )}
            </div>
          </>
        )}
      </Panel>

      <Panel
        title={t('project.schedule.budget.title')}
        description={t('project.schedule.budget.description')}
      >
        <div className="overflow-x-auto">
          <table className="w-full border-collapse text-sm">
            <caption className="dhd-sr-only">{t('project.schedule.budget.caption')}</caption>
            <thead>
              <tr>
                <th scope="col" className={cn(HEAD_CELL, 'text-left')}>
                  {t('project.schedule.col.phase')}
                </th>
                <th scope="col" className={cn(HEAD_CELL, 'text-right')}>
                  {t('project.schedule.col.budgeted')}
                </th>
                <th scope="col" className={cn(HEAD_CELL, 'text-right')}>
                  {t('project.schedule.col.actual')}
                </th>
                <th scope="col" className={cn(HEAD_CELL, 'text-right')}>
                  {t('project.schedule.col.variance')}
                </th>
              </tr>
            </thead>
            <tbody>
              {phases.map((phase) => {
                const variance = phase.actual - phase.budgeted;
                return (
                  <tr key={phase.id} className="border-b border-line-subtle">
                    <th
                      scope="row"
                      className="px-3 py-2 text-left font-body text-sm font-normal text-body"
                    >
                      {phase.name}
                    </th>
                    <td className="px-3 py-2 text-right tabular-nums text-body">
                      {formatMoney(phase.budgeted, { precision: 0 })}
                    </td>
                    <td className="px-3 py-2 text-right tabular-nums text-body">
                      {formatMoney(phase.actual, { precision: 0 })}
                    </td>
                    <td
                      className={cn(
                        'px-3 py-2 text-right tabular-nums',
                        varianceTone(phase.actual, variance),
                      )}
                    >
                      {formatMoney(variance, { precision: 0 })}
                    </td>
                  </tr>
                );
              })}
            </tbody>
            <tfoot>
              <tr className="border-t border-line-strong">
                <th
                  scope="row"
                  className="px-3 py-2 text-left font-display text-2xs font-semibold uppercase tracking-label text-heading"
                >
                  {t('project.schedule.total')}
                </th>
                <td className="px-3 py-2 text-right font-medium tabular-nums text-heading">
                  {formatMoney(totals.budgeted, { precision: 0 })}
                </td>
                <td className="px-3 py-2 text-right font-medium tabular-nums text-heading">
                  {formatMoney(totals.actual, { precision: 0 })}
                </td>
                <td
                  className={cn(
                    'px-3 py-2 text-right font-medium tabular-nums',
                    totals.variance <= 0 ? 'text-success' : 'text-danger',
                  )}
                >
                  {formatMoney(totals.variance, { precision: 0 })}
                </td>
              </tr>
            </tfoot>
          </table>
        </div>
      </Panel>
    </div>
  );
}

/* -------------------------------------------------------------------------- */

/**
 * One phase: its label and money, then two stacked lanes and a milestone lane.
 *
 * Two lanes rather than one is the whole point of the row — a single bar would
 * leave a phase that is running late indistinguishable from one that is simply
 * long, and on a page about parallelism that ambiguity is the defect.
 */
function PhaseRow({
  phase,
  months,
  extent,
  milestones,
}: {
  phase: SchedulePhase;
  months: number;
  extent: number;
  milestones: Milestone[];
}) {
  const t = useT();
  const status = STATUS_META[phase.status];
  const planned: Span = {
    start: phase.plannedStart,
    end: phase.plannedStart + phase.plannedWidth,
  };
  const actual = hasActual(phase) ? workSpan(phase) : null;
  const variance = phase.actual - phase.budgeted;
  const markers = markerRun(milestones, actual ?? planned);

  const at = (value: number) => `${(value / extent) * 100}%`;

  return (
    <li className={cn(ROW_GRID, 'border-b border-line-subtle')}>
      <div className="min-w-0 py-3">
        <div className="flex flex-wrap items-center gap-2">
          <span className="font-display text-sm font-semibold uppercase tracking-label text-heading">
            {phase.name}
          </span>
          <Badge tone={status.tone} variant="subtle" size="sm">
            {t(status.label)}
          </Badge>
        </div>
        {/* Each figure is its own flex item: a break is allowed between the
            figures, never inside one — `$ 141,100` split across two lines reads
            as two numbers. */}
        <p className="mt-1 flex flex-wrap items-center gap-x-2.5 gap-y-0.5 text-2xs tabular-nums text-muted">
          <span>
            {t('project.schedule.amount.budgeted', {
              amount: formatMoney(phase.budgeted, { precision: 0 }),
            })}
          </span>
          <span>
            {t('project.schedule.amount.actual', {
              amount: formatMoney(phase.actual, { precision: 0 }),
            })}
          </span>
          <span className={varianceTone(phase.actual, variance)}>
            {t('project.schedule.amount.variance', {
              amount: formatMoney(variance, { precision: 0 }),
            })}
          </span>
        </p>
        <p className="dhd-sr-only">
          {phase.name}, {t(status.label)}.{' '}
          {t('project.schedule.sr.planned', {
            from: monthAt(planned.start, months),
            to: monthAt(planned.end, months),
          })}{' '}
          {actual
            ? t('project.schedule.sr.actual', {
                from: monthAt(actual.start, months),
                to: monthAt(actual.end, months),
              })
            : t('project.schedule.sr.notStarted')}
          {/* Explicit, because JSX drops the newline between the expression above
              and this text and the two sentences would run together. */}
          {' '}
          {t('project.schedule.sr.figures', {
            budgeted: formatMoney(phase.budgeted, { precision: 0 }),
            actual: formatMoney(phase.actual, { precision: 0 }),
            variance: formatMoney(variance, { precision: 0 }),
          })}
          {markers.length === 0
            ? ''
            : ` ${t('project.schedule.sr.milestones', {
                list: markers
                  .map(({ milestone }) =>
                    t('project.schedule.sr.milestone', {
                      title: milestone.title,
                      date: formatDate(milestone.date),
                      status:
                        milestone.status === 'reached'
                          ? t('project.schedule.milestone.reached')
                          : t('project.schedule.milestone.upcoming'),
                    }),
                  )
                  .join('; '),
              })}`}
        </p>
      </div>

      <div className="relative flex min-w-0 flex-col justify-center gap-1">
        {/* One gridline per month, drawn behind the bars of this row. Rows are
            adjacent and border-to-border, so the lines meet across the whole
            chart and read as a single grid rather than nine separate ones. */}
        <div aria-hidden="true" className="pointer-events-none absolute inset-0 flex">
          {Array.from({ length: months }, (_, index) => (
            <span
              key={index}
              className={cn('h-full flex-1', index > 0 && 'border-l border-line-subtle')}
            />
          ))}
        </div>

        <div className="relative h-3">
          <span
            className="absolute inset-y-0 rounded-xs bg-chart-2"
            style={{ left: at(planned.start), width: at(planned.end - planned.start) }}
          />
        </div>

        <div className="relative h-3">
          {actual ? (
            <span
              className="absolute inset-y-0 rounded-xs bg-chart-1"
              style={{ left: at(actual.start), width: at(actual.end - actual.start) }}
            />
          ) : (
            <span className="absolute inset-y-0 left-0 flex items-center text-2xs leading-none text-muted">
              {t('project.schedule.notStarted')}
            </span>
          )}
        </div>

        <div className="relative h-3.5">
          {markers.map(({ milestone, position }) => (
            <Tooltip
              key={milestone.id}
              content={`${milestone.title} · ${formatDate(milestone.date)}`}
              trigger={
                <span
                  role="img"
                  aria-label={`${milestone.title}, ${formatDate(milestone.date)}, ${
                    milestone.status === 'reached'
                      ? t('project.schedule.milestone.reached')
                      : t('project.schedule.milestone.upcoming')
                  }`}
                  className={cn(
                    'absolute size-2 rotate-45',
                    milestone.status === 'reached'
                      ? 'bg-success'
                      : 'border border-line-strong bg-surface',
                    PRESS,
                    FOCUS_RING,
                  )}
                  style={{
                    left: `calc(${at(position)} - 0.25rem)`,
                    top: 'calc(50% - 0.25rem)',
                  }}
                />
              }
            />
          ))}
        </div>
      </div>
    </li>
  );
}
